<template>
  <div class="issues-category-detail" :data-wdtype="wdtype">
    <!-- Header: Title + New badge -->
    <div class="issues-category-header">
      <h2 class="issues-category-title">
        <span v-if="newAlertCount > 0" class="issues-category-new-badge">{{ newAlertCount }} new</span>
        {{ categoryName }}
      </h2>
    </div>

    <!-- Filter bar: Resolved | Active | Search | Mark all seen -->
    <div class="issues-category-toolbar">
      <div class="issues-category-tabs">
        <button
          v-if="!resolvedHidden"
          :class="['issues-category-tab', { active: issuesListTab === 'resolved' }]"
          @click="$emit('issues-tab-change', 'resolved')"
        >
          <i class="check icon"></i> Resolved <span class="tab-count">{{ resolvedCount }}</span>
        </button>
        <button
          :class="['issues-category-tab', 'active-tab', { active: issuesListTab === 'active' || resolvedHidden, 'has-active-count': activeCount > 0 }]"
          @click="$emit('issues-tab-change', 'active')"
        >
          {{ activeCount }} Active
        </button>
      </div>

      <div class="issues-category-actions">
        <div class="issues-category-marketplace-filter">
          <i class="globe icon"></i>
          <select
            class="issues-category-marketplace-select"
            :value="marketplaceColumnFilterValue"
            aria-label="Filter by marketplace"
            @change="onMarketplaceColumnFilter"
          >
            <option value="">All marketplaces</option>
            <option v-for="c in marketplaceFilterOptions" :key="c" :value="c">
              {{ marketplaceOptionLabel(c) }}
            </option>
          </select>
        </div>
        <div v-if="wdtype === 'fbaFeeChange'" class="issues-category-marketplace-filter">
          <i class="sort amount down icon"></i>
          <select
            class="issues-category-marketplace-select"
            :value="feeDirection"
            aria-label="Sort by FBA fee change"
            @change="onFeeDirectionChange"
          >
            <option value="">Newest first</option>
            <option value="increase">Fee increase (largest first)</option>
            <option value="decrease">Fee decrease (largest first)</option>
          </select>
        </div>
        <div class="issues-category-search">
          <i class="search icon"></i>
          <input
            type="search"
            v-model="searchQuery"
            v-prevent-autofill="searchQuery"
            placeholder="Search products..."
            @input="onSearchInput"
            class="issues-category-search-input"
          />
        </div>
        <button
          v-if="wdtype === 'salesDrop'"
          type="button"
          class="issues-category-configure-btn"
          :class="{ open: showSalesConfigurePanel }"
          @click="toggleSalesConfigurePanel"
        >
          <i class="setting icon"></i>
          Configure Thresholds
        </button>
        <button
          v-if="wdtype === 'competitivePrice'"
          type="button"
          class="issues-category-configure-btn"
          :disabled="exportingChangeLog"
          title="Every change of Amazon's competitive price in the last 30 days (CSV per storefront)"
          @click="exportChangeLog"
        >
          <i :class="exportingChangeLog ? 'notched circle loading icon' : 'download icon'"></i>
          Export change log
        </button>
        <p v-if="exportChangeLogError" class="issues-category-export-error">{{ exportChangeLogError }}</p>
        <button
          v-if="canMarkAllResolved"
          type="button"
          class="issues-category-configure-btn"
          :disabled="bulkResolveInProgress"
          @click="markAllResolved"
        >
          <i v-if="bulkResolveInProgress" class="notched circle loading icon"></i>
          {{ itemsMeta && itemsMeta.total_pages > 1 ? 'Mark page as resolved' : 'Mark all as resolved' }}
        </button>
      </div>
    </div>

    <template v-if="wdtype === 'salesDrop'">
      <div
        v-if="showSalesConfigurePanel && !salesVolumeThresholdPanelDataReady"
        class="sales-volume-thresholds-prefetch-bar"
        role="status"
        aria-live="polite"
      >
        <i class="notched circle loading icon" />
        <span>Loading sales volume thresholds…</span>
      </div>
      <div
        v-if="showSalesConfigurePanel"
        :class="{ 'sales-volume-thresholds-panel-wrap--prefetch': !salesVolumeThresholdPanelDataReady }"
      >
        <SalesVolumeThresholdsPanel
          :panel-open="showSalesConfigurePanel"
          :thresholds-api-base="salesVolumeThresholdsApiBaseUrl"
          :seller-account-id="stockThresholdSellerAccountId"
          @close="closeSalesConfigurePanel"
          @saved="onSalesVolumePanelSaved"
          @thresholds-load-complete="onSalesVolumeThresholdsLoadComplete"
        />
      </div>
    </template>

    <!-- Issue cards list -->
    <div
      class="issues-category-list"
      ref="issuesListScroll"
      @scroll.passive="onIssuesListScroll"
    >
      <div v-if="displayedItems.length > 0" class="issues-category-list-head" aria-hidden="true">
        <span class="issues-col-product">Product</span>
        <span class="issues-col-marketplace">Marketplace</span>
        <span class="issues-col-change">Change</span>
        <span class="issues-col-date">Date</span>
      </div>
      <div
        v-if="listVirtualPadTop > 0"
        class="issues-list-virtual-spacer"
        :style="{ height: listVirtualPadTop + 'px' }"
        aria-hidden="true"
      ></div>
      <IssuesCategoryCard
        v-for="item in windowedDisplayedItems"
        :key="item.watchDogItemId || item.watchDogId"
        :item="item"
        :wdtype="wdtype"
        :issues-list-tab="issuesListTab"
        :products="products"
        :status-label="getStatusLabel(item)"
        :status-class="getStatusClass(item)"
        :api-base-url="alertThresholdsApiBaseUrl"
        :stock-alert-thresholds-api-base-url="stockAlertThresholdsApiBaseUrl"
        :sales-volume-thresholds-api-base-url="salesVolumeThresholdsApiBaseUrl"
        :seller-account-id-for-thresholds="stockThresholdSellerAccountId"
        :mark-resolve-busy="!!markResolveBusyIds[item.watchDogId]"
        :highlighted-alert-id="highlightedAlertId"
        :filter-by-market="filterByMarket"
        @mute="muteItem"
        @view-product="viewProduct"
        @saved="onAlertThresholdsSaved"
        @mark-resolved="markItemResolved"
        @filter-marketplace="onMarketplaceChipFilter"
      />
      <div
        v-if="listVirtualPadBottom > 0"
        class="issues-list-virtual-spacer"
        :style="{ height: listVirtualPadBottom + 'px' }"
        aria-hidden="true"
      ></div>

      <div v-if="itemsLoading && displayedItems.length === 0" class="issues-category-empty">
        Loading alerts…
      </div>
      <div v-else-if="displayedItems.length === 0" class="issues-category-empty">
        <template v-if="issuesListTab === 'active'">No current alerts</template>
        <template v-else>No resolved issues</template>
      </div>
      <div v-if="itemsMeta && itemsMeta.total_pages > 1" class="issues-category-pager">
        <button type="button" class="issues-category-pager-btn" :disabled="itemsMeta.page <= 1" @click="$emit('issues-page-change', itemsMeta.page - 1)">Previous</button>
        <span>Page {{ itemsMeta.page }} of {{ itemsMeta.total_pages }}</span>
        <button type="button" class="issues-category-pager-btn" :disabled="itemsMeta.page >= itemsMeta.total_pages" @click="$emit('issues-page-change', itemsMeta.page + 1)">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import IssuesCategoryCard from './IssuesCategoryCard.vue'
import SalesVolumeThresholdsPanel from './SalesVolumeThresholdsPanel.vue'
import {
  filterIssuesItemsGlobally,
  isCompetitorPriceOpenChange,
  isIssuesActiveTabItem,
  isIssuesResolvedTabItem,
  sortIssuesItemsForTab,
  countIssuesNewAlertsFiltered
} from '../../utils/issuesAlertFilters.js'
import { buildProductDetailsQuery } from '../../utils/listViewUrlSync.js'
import { marketplaceColumnLabel, normalizeMarketplaceCode } from '../../utils/amazonMarketplaceUrls.js'
import { parseSummaryJsonPayload, watchdogTypeToApiParam } from '../../utils/watchdogSummaryApi.js'
import './styles/IssuesCategoryDetail.css'

/** Max issue cards mounted at once (API page is also 10). */
var LIST_WINDOW = 10
/** Approximate card height for virtual spacer math. */
var LIST_ROW_HEIGHT = 88

function readCents (details, snakeKey, camelKey) {
  var v = details[snakeKey] != null ? details[snakeKey] : details[camelKey]
  if (v == null || v === '') return null
  var n = Number(v)
  return isNaN(n) ? null : n
}

/** Signed FBA fee change in cents (current - previous); null when unknown. Mirrors API fee_direction ordering. */
function fbaFeeDeltaCents (item) {
  var d = (item && item.statusDetails) || {}
  var prev = readCents(d, 'previous_fba_fulfillment_fee_cents', 'previousFbaFulfillmentFeeCents')
  if (prev == null) prev = readCents(d, 'previous_fba_fee_cents', 'previousFbaFeeCents')
  var curr = readCents(d, 'current_fba_fulfillment_fee_cents', 'currentFbaFulfillmentFeeCents')
  if (curr == null) curr = readCents(d, 'current_fba_fee_cents', 'currentFbaFeeCents')
  if (prev == null || curr == null) return null
  return curr - prev
}

function competitorPriceChangeLabel (item) {
  var d = (item && item.statusDetails) || {}
  var change = d.last_competitor_change || d.lastCompetitorChange || {}
  var oldC = Number(change.old_cents != null ? change.old_cents : change.oldCents)
  var newC = Number(change.new_cents != null ? change.new_cents : change.newCents)
  if (!isFinite(oldC) || !isFinite(newC)) return 'Price change'
  if (newC > oldC) return 'Increased'
  if (newC < oldC) return 'Decreased'
  return 'Price change'
}

/** Walk ancestors until IndexPage (`worldStore` + `wdList`). */
function resolveIndexPageAncestor (vm) {
  var p = vm.$parent
  while (p) {
    if (p.worldStore && Array.isArray(p.wdList)) return p
    p = p.$parent
  }
  return null
}

const WD_INFO = {
  lostBuybox: { name: 'Buy Box Issues', ccf: '30 minutes' },
  price: { name: 'Price Change', ccf: '15 minutes' },
  numberOfSellers: { name: 'Hijacker Alert', ccf: '15 minutes' },
  listingSuspended: { name: 'Suspended Listings', ccf: '10 minutes' },
  searchBan: { name: 'Search Suppression', ccf: '3 hours' },
  keyword: { name: 'Keyword Tracking', ccf: '—' },
  noAddToCart: { name: 'Add-to-Cart Button', ccf: '30 minutes' },
  productDetailsRecentChange: { name: 'Product Details Change', ccf: '30 minutes' },
  imageMissing: { name: 'Main Image', ccf: '30 minutes' },
  productCategoryRecentChange: { name: 'Category Change', ccf: '1.5 hours' },
  parentProductChange: { name: 'Parent Product Change', ccf: '31 minutes' },
  adultClassification: { name: 'Adult Classification', ccf: '30 minutes' },
  inventory: { name: 'FBA Stock', ccf: '90 minutes' },
  fbmInventory: { name: 'FBM Stock', ccf: '90 minutes' },
  salesDrop: { name: 'Sales Volume', ccf: '30 minutes' },
  fbaFeeChange: { name: 'FBA Fee Change', ccf: '1 day' },
  competitorPrice: { name: 'Competitor Price', ccf: 'Real-time' },
  competitivePrice: { name: 'Competitive Price', ccf: 'Real-time' },
  reviewHealth: { name: 'Review Health', ccf: '1 day' },
  bestSellerBadge: { name: 'Best Seller Badge', ccf: '30 minutes' },
  prime: { name: 'Prime Badge', ccf: '30 minutes' },
  amazonChoiceBadge: { name: "Amazon's Choice", ccf: '30 minutes' },
  subscribeAndSave: { name: 'Subscribe & Save', ccf: '30 minutes' },
  badReview: { name: 'Latest Reviews', ccf: '2 hours' },
  topReview: { name: 'Top Rated Reviews', ccf: '4 hours' },
  starRatingRecentChange: { name: 'Star Rating', ccf: '2.5 hours' },
  badSellerFeedback: { name: 'Bad Seller Feedback', ccf: '2 hours' }
}

export default {
  name: 'IssuesCategoryDetail',
  components: { IssuesCategoryCard, SalesVolumeThresholdsPanel },
  props: {
    wdtype: { type: String, required: true },
    wd: { type: Object, required: true },
    filterByMarket: { type: Array, default: () => [] },
    filterBySellAcc: { type: Array, default: () => [] },
    dateRange: { type: String, default: 'today' },
    alertThresholdsApiBaseUrl: { type: String, default: '/api/v1/watchdogs' },
    stockAlertThresholdsApiBaseUrl: { type: String, default: '/api/v1/stock-alert-thresholds' },
    salesVolumeThresholdsApiBaseUrl: { type: String, default: '/api/v1/sales-volume-alert-thresholds' },
    issuesListTab: { type: String, default: 'active' },
    /** Whale catalogs: hide Resolved tab (API meta.resolved_hidden). */
    resolvedHidden: { type: Boolean, default: false },
    /** Products embedded from GET /watchdogs/summary/items (keyed by catalog product id string). */
    issueProducts: { type: Object, default: () => ({}) },
    marketplaceFilterOptions: { type: Array, default: function () { return [] } },
    highlightedAlertId: { type: String, default: '' },
    itemsMeta: { type: Object, default: null },
    /** fbaFeeChange only: '' | 'increase' | 'decrease' (server filters + orders by fee delta). */
    feeDirection: { type: String, default: '' },
    itemsLoading: { type: Boolean, default: false }
  },
  data: function () {
    return {
      searchQuery: '',
      bulkResolveInProgress: false,
      showSalesConfigurePanel: false,
      salesVolumeThresholdPanelDataReady: true,
      markResolveBusyIds: {},
      listWindowStart: 0,
      exportingChangeLog: false,
      exportChangeLogError: ''
    }
  },
  computed: {
    categoryName: function () {
      return (WD_INFO[this.wdtype] && WD_INFO[this.wdtype].name) || this.wdtype
    },
    products: function () {
      var idx = resolveIndexPageAncestor(this)
      var base = (idx && idx.worldStore && idx.worldStore.products) || {}
      var extra = this.issueProducts || {}
      if (!extra || typeof extra !== 'object' || Object.keys(extra).length === 0) return base
      return Object.assign({}, base, extra)
    },
    activeItems: function () {
      return this.getFilteredItems('active')
    },
    resolvedItems: function () {
      return this.getFilteredItems('resolved')
    },
    activeCount: function () {
      var st = this.wd.summaryTotals
      if (
        st &&
        typeof st.activeRed === 'number' &&
        typeof st.activeYellow === 'number'
      ) {
        return st.activeRed + st.activeYellow
      }
      return this.activeItems.length
    },
    resolvedCount: function () {
      var st = this.wd.summaryTotals
      if (st && typeof st.resolved === 'number') {
        return st.resolved
      }
      return this.resolvedItems.length
    },
    newAlertCount: function () {
      return countIssuesNewAlertsFiltered(
        this.wd.items,
        this.products,
        this.filterByMarket,
        this.filterBySellAcc,
        this.dateRange
      )
    },
    displayedItems: function () {
      if (this.resolvedHidden || this.issuesListTab === 'active') return this.filterBySearch(this.activeItems)
      return this.filterBySearch(this.resolvedItems)
    },
    windowedDisplayedItems: function () {
      var all = this.displayedItems || []
      if (all.length <= LIST_WINDOW) return all
      var start = Math.max(0, Math.min(this.listWindowStart, Math.max(0, all.length - LIST_WINDOW)))
      return all.slice(start, start + LIST_WINDOW)
    },
    listVirtualPadTop: function () {
      var all = this.displayedItems || []
      if (all.length <= LIST_WINDOW) return 0
      var start = Math.max(0, Math.min(this.listWindowStart, Math.max(0, all.length - LIST_WINDOW)))
      return start * LIST_ROW_HEIGHT
    },
    listVirtualPadBottom: function () {
      var all = this.displayedItems || []
      if (all.length <= LIST_WINDOW) return 0
      var start = Math.max(0, Math.min(this.listWindowStart, Math.max(0, all.length - LIST_WINDOW)))
      var after = Math.max(0, all.length - (start + LIST_WINDOW))
      return after * LIST_ROW_HEIGHT
    },
    stockThresholdSellerAccountId: function () {
      if (this.filterBySellAcc && this.filterBySellAcc.length === 1) return String(this.filterBySellAcc[0])
      return null
    },
    canMarkAllResolved: function () {
      if (this.issuesListTab !== 'active') return false
      return this.displayedItems.some(function (item) {
        return !!(item && item.watchDogId && !item.muted && item.issueStatus !== 3)
      })
    },
    marketplaceColumnFilterValue: function () {
      var m = this.filterByMarket || []
      if (m.length === 1) return normalizeMarketplaceCode(m[0])
      return ''
    }
  },
  watch: {
    displayedItems: function () {
      this.listWindowStart = 0
    },
    issuesListTab: function () {
      this.listWindowStart = 0
    }
  },
  methods: {
    onIssuesListScroll: function () {
      var all = this.displayedItems || []
      if (all.length <= LIST_WINDOW) {
        this.listWindowStart = 0
        return
      }
      var el = this.$refs.issuesListScroll
      if (!el) return
      var next = Math.floor(el.scrollTop / LIST_ROW_HEIGHT)
      var maxStart = Math.max(0, all.length - LIST_WINDOW)
      if (next < 0) next = 0
      if (next > maxStart) next = maxStart
      if (next !== this.listWindowStart) this.listWindowStart = next
    },
    getFilteredItems: function (tab) {
      var items = this.wd.items || {}
      var arr = Object.values(items)
      arr = filterIssuesItemsGlobally(arr, this.products, this.filterByMarket, this.filterBySellAcc, this.dateRange)

      if (tab === 'active') {
        arr = arr.filter(isIssuesActiveTabItem)
      } else {
        /* Resolved = OK tab from old Watchdog: products without issues (status 0 or 99) */
        arr = arr.filter(isIssuesResolvedTabItem)
      }

      if (this.wdtype === 'fbaFeeChange' && this.feeDirection) {
        var dir = this.feeDirection === 'decrease' ? 1 : -1
        return arr.sort(function (a, b) {
          var da = fbaFeeDeltaCents(a)
          var db = fbaFeeDeltaCents(b)
          if (da == null) return db == null ? 0 : 1
          if (db == null) return -1
          return dir * (da - db)
        })
      }
      return sortIssuesItemsForTab(arr, tab, this.products)
    },
    onFeeDirectionChange: function (e) {
      this.$emit('issues-fee-direction-change', e && e.target ? e.target.value : '')
    },
    onSearchInput: function () {
      this.$emit('issues-search-change', this.searchQuery)
    },
    filterBySearch: function (arr) {
      if (!this.searchQuery.trim()) return arr
      var q = this.searchQuery.toLowerCase()
      var products = this.products
      return arr.filter(function (item) {
        var prod = item.productId && products[item.productId] ? products[item.productId] : null
        var asin = ''
        var title = ''
        var mp = normalizeMarketplaceCode(item.marketplace)
        if (prod) {
          title = (prod.productTitle || '').toLowerCase()
          asin = (prod.asin || '').toLowerCase()
          if (!asin) {
            var fromUrl = (prod.productUrl || '').match(/\/dp\/([A-Z0-9]+)/i)
            asin = fromUrl ? fromUrl[1].toLowerCase() : ''
          }
          if (!mp) mp = normalizeMarketplaceCode(prod.marketplace)
        }
        if (!asin && item.asin) asin = String(item.asin).toLowerCase()
        var mpLabel = mp ? (mp + ' marketplace').toLowerCase() : ''
        if (title.indexOf(q) >= 0 || asin.indexOf(q) >= 0 || mp.toLowerCase().indexOf(q) >= 0 || mpLabel.indexOf(q) >= 0) {
          return true
        }
        var blob = ''
        if (item.title) blob += ' ' + String(item.title)
        if (item.statusDetails && item.statusDetails.message) blob += ' ' + String(item.statusDetails.message)
        return blob.toLowerCase().indexOf(q) >= 0
      })
    },
    marketplaceOptionLabel: function (code) {
      return marketplaceColumnLabel(code) || code
    },
    onMarketplaceColumnFilter: function (e) {
      var v = e && e.target ? e.target.value : ''
      this.$emit('filter-market', v ? [v] : [])
    },
    onMarketplaceChipFilter: function (code) {
      var next = normalizeMarketplaceCode(code)
      if (!next) return
      var current = (this.filterByMarket || []).map(normalizeMarketplaceCode)
      if (current.length === 1 && current[0] === next) {
        this.$emit('filter-market', [])
        return
      }
      this.$emit('filter-market', [next])
    },
    getStatusLabel: function (item) {
      if (item.muted) return 'Muted'
      if (isCompetitorPriceOpenChange(item)) return competitorPriceChangeLabel(item)
      if (item.issueStatus === 3 || item.status === 99) return 'RESOLVED'
      if (
        this.issuesListTab === 'resolved' &&
        isIssuesResolvedTabItem(item)
      ) {
        return 'RESOLVED'
      }
      if (this.wdtype === 'lostBuybox' && item.statusDetails) {
        var msg = (item.statusDetails.message || '').toLowerCase()
        var d = item.statusDetails
        if (msg.indexOf('suppress') >= 0) return 'SUPPRESSED'
        if (msg.indexOf('lost') >= 0 || msg.indexOf('another seller') >= 0 || d.lost_to_another_seller || d.lost_to_amazon || d.lost_to_whitelisted_seller) return 'LOST'
        if (msg.indexOf('rotat') >= 0) return 'ROTATING'
        if (d.has_own_offer && !d.lost_to_another_seller && !d.lost_to_amazon && !d.lost_to_whitelisted_seller) return 'ROTATING'
      }
      if (item.status === 2) return 'Alert'
      if (item.status === 1) return 'Warning'
      return 'OK'
    },
    getStatusClass: function (item) {
      if (item.muted) return 'muted'
      if (isCompetitorPriceOpenChange(item)) return 'warning'
      if (item.issueStatus === 3 || item.status === 99) return 'resolved'
      if (
        this.issuesListTab === 'resolved' &&
        isIssuesResolvedTabItem(item)
      ) {
        return 'resolved'
      }
      if (this.wdtype === 'lostBuybox' && item.statusDetails) {
        var msg = (item.statusDetails.message || '').toLowerCase()
        var d = item.statusDetails
        if (msg.indexOf('suppress') >= 0) return 'suppressed'
        if (msg.indexOf('lost') >= 0 || msg.indexOf('another seller') >= 0 || d.lost_to_another_seller || d.lost_to_amazon || d.lost_to_whitelisted_seller) return 'lost'
        if (msg.indexOf('rotat') >= 0) return 'rotating'
        if (d.has_own_offer && !d.lost_to_another_seller && !d.lost_to_amazon && !d.lost_to_whitelisted_seller) return 'rotating'
      }
      if (item.status === 2) return 'alert'
      if (item.status === 1) return 'warning'
      return 'ok'
    },
    /** `activeCount` / `resolvedCount` prefer API `summaryTotals`; bump locally on resolve. */
    applyLocalSummaryTotalsForResolve: function (item, count) {
      var n = count != null ? count : 1
      var st = this.wd && this.wd.summaryTotals
      if (!st) return
      var next = Object.assign({}, st)
      var isYellow = item && (item.status === 1 || item.status === '1')
      if (isYellow && typeof next.activeYellow === 'number') {
        next.activeYellow = Math.max(0, next.activeYellow - n)
      } else if (typeof next.activeRed === 'number') {
        next.activeRed = Math.max(0, next.activeRed - n)
      }
      if (typeof next.resolved === 'number') {
        next.resolved = next.resolved + n
      }
      this.$set(this.wd, 'summaryTotals', next)
    },
    /** Sidebar badge delta when API omits `alert_counts_delta`. */
    synthesizeAlertCountsDelta: function (item, count) {
      var n = count != null ? count : 1
      var isYellow = item && (item.status === 1 || item.status === '1')
      return {
        watchdog_type: watchdogTypeToApiParam(this.wdtype),
        red: isYellow ? 0 : -n,
        yellow: isYellow ? -n : 0
      }
    },
    markItemResolved: function (item) {
      if (!item || !item.watchDogId || item.issueStatus === 3) return
      var wid = item.watchDogId
      if (this.markResolveBusyIds[wid]) return
      this.$set(this.markResolveBusyIds, wid, true)
      var indexPage = resolveIndexPageAncestor(this)
      var self = this
      if (!indexPage || !indexPage.stateChangeRequestInFlight) {
        self.$delete(self.markResolveBusyIds, wid)
        return
      }
      var timestamp = Date.now()
      indexPage.stateChangeRequestInFlight.add(timestamp)
      this.$http.post('/api/sentinel/watchdog/set-issue-status/' + encodeURIComponent(String(wid)) + '/3', {}).then(
        function (response) {
          var prevStatus = item.status
          item.issueStatus = 3
          self.applyLocalSummaryTotalsForResolve({ status: prevStatus }, 1)
          indexPage.stateChangeRequestInFlight.delete(timestamp)
          var body = parseSummaryJsonPayload(response)
          if (body && body.alert_counts_delta) {
            self.$emit('alert-counts-delta', body.alert_counts_delta)
          } else {
            self.$emit('alert-counts-delta', self.synthesizeAlertCountsDelta({ status: prevStatus }, 1))
          }
          self.$delete(self.markResolveBusyIds, wid)
        },
        function () {
          indexPage.stateChangeRequestInFlight.delete(timestamp)
          self.$delete(self.markResolveBusyIds, wid)
        }
      )
    },
    muteItem: function (item, minutes) {
      var self = this
      var indexPage = resolveIndexPageAncestor(this)
      if (indexPage && indexPage.stateChangeRequestInFlight) {
        var timestamp = Date.now()
        indexPage.stateChangeRequestInFlight.add(timestamp)
        this.$http.post('/api/sentinel/watchdog/' + (minutes < 1 ? 'un' : '') + 'mute/' + item.watchDogId, { minutes: minutes })
          .then(function () {
            item.muted = minutes >= 1
            indexPage.stateChangeRequestInFlight.delete(timestamp)
          }, function () {
            indexPage.stateChangeRequestInFlight.delete(timestamp)
          })
      }
    },
    /** Resolve the visible page via set-issue-status-bulk; on 200 reload so the next page of alerts slides in. */
    markAllResolved: function () {
      var resolveTargets = this.displayedItems.filter(function (item) {
        return !!(item && item.watchDogId && !item.muted && item.issueStatus !== 3)
      })
      var issueIds = resolveTargets.map(function (item) { return item.watchDogId })
      if (!issueIds.length) return
      var indexPage = resolveIndexPageAncestor(this)
      if (!indexPage || !indexPage.stateChangeRequestInFlight) return

      var timestamp = Date.now()
      indexPage.stateChangeRequestInFlight.add(timestamp)
      this.bulkResolveInProgress = true

      this.$http.post('/api/sentinel/watchdog/set-issue-status-bulk', { issue_ids: issueIds, status: 'resolved' })
        .then(function (response) {
          this.bulkResolveInProgress = false
          indexPage.stateChangeRequestInFlight.delete(timestamp)
          if (response.status !== 200) return

          var yellowN = 0
          var redN = 0
          resolveTargets.forEach(function (entry) {
            entry.issueStatus = 3
            if (entry.status === 1 || entry.status === '1') yellowN += 1
            else redN += 1
          })
          var st = this.wd.summaryTotals
          if (st) {
            var next = Object.assign({}, st)
            if (typeof next.activeYellow === 'number') next.activeYellow = Math.max(0, next.activeYellow - yellowN)
            if (typeof next.activeRed === 'number') next.activeRed = Math.max(0, next.activeRed - redN)
            if (typeof next.resolved === 'number') next.resolved = next.resolved + issueIds.length
            this.$set(this.wd, 'summaryTotals', next)
          }
          this.$emit('alert-counts-delta', {
            watchdog_type: watchdogTypeToApiParam(this.wdtype),
            red: -redN,
            yellow: -yellowN
          })
          /** Resolved rows leave the active list, so refetching the same page number returns the next batch. */
          this.$emit('issues-summary-refresh-request')
        }.bind(this), function () {
          this.bulkResolveInProgress = false
          indexPage.stateChangeRequestInFlight.delete(timestamp)
        }.bind(this))
    },
    viewProduct: function (productId) {
      if (!productId) return
      var cleanId = String(productId).replace(/^PROD-/i, '')
      var issuesPage = this.$parent
      if (issuesPage && typeof issuesPage.syncIssuesRouteQuery === 'function') {
        issuesPage.syncIssuesRouteQuery()
      }
      this.$router.push({ path: '/product_details', query: buildProductDetailsQuery(cleanId, this.$route.fullPath) })
    },
    /** Stock FBA/FBM / sales-volume inlines already call their APIs and patch local `item.settings`. No SOTW or watchdog refetch — avoids full UI refresh / reload feel. */
    onAlertThresholdsSaved: function () {},
    /** competitivePrice: download GET /api/v1/sellers/:id/competitive-price-changes.csv (last 30 days) for the
     *  filtered storefront, or for every storefront on the account. */
    exportChangeLog: function () {
      var self = this
      if (self.exportingChangeLog) return
      self.exportingChangeLog = true
      self.exportChangeLogError = ''
      var to = new Date()
      var from = new Date(to.getTime() - 29 * 24 * 3600 * 1000)
      var iso = function (d) { return d.toISOString().slice(0, 10) }
      var params = { from: iso(from), to: iso(to) }
      if (self.filterByMarket && self.filterByMarket.length === 1) params.marketplace = self.filterByMarket[0]
      var sellerIds = self.filterBySellAcc && self.filterBySellAcc.length
        ? Promise.resolve(self.filterBySellAcc.map(String))
        : self.$http.get('/api/v1/sellers').then(function (res) {
          return ((res.body && res.body.item) || []).map(function (it) {
            return String(it.sellerId && it.sellerId.value !== undefined ? it.sellerId.value : it.sellerId)
          })
        })
      sellerIds.then(function (ids) {
        return ids.reduce(function (chain, id) {
          return chain.then(function () {
            return self.$http.get('/api/v1/sellers/' + encodeURIComponent(id) + '/competitive-price-changes.csv',
              { params: params, responseType: 'blob' }).then(function (res) {
              var name = 'competitive-price-changes-' + id + '-' + params.from + '-' + params.to + '.csv'
              var cd = res.headers && res.headers.get && res.headers.get('Content-Disposition')
              var m = cd && cd.match(/filename="([^"]+)"/)
              if (m) name = m[1]
              var url = window.URL.createObjectURL(res.body)
              var a = document.createElement('a')
              a.href = url
              a.download = name
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              window.URL.revokeObjectURL(url)
            })
          })
        }, Promise.resolve())
      }).then(function () {
        self.exportingChangeLog = false
      }, function () {
        self.exportingChangeLog = false
        self.exportChangeLogError = 'Export failed. Please try again.'
      })
    },
    toggleSalesConfigurePanel: function () {
      this.showSalesConfigurePanel = !this.showSalesConfigurePanel
      if (this.showSalesConfigurePanel) {
        this.salesVolumeThresholdPanelDataReady = false
      }
    },
    closeSalesConfigurePanel: function () {
      this.showSalesConfigurePanel = false
    },
    onSalesVolumeThresholdsLoadComplete: function () {
      this.salesVolumeThresholdPanelDataReady = true
    },
    onSalesVolumePanelSaved: function () {
      this.showSalesConfigurePanel = false
    }
  }
}
</script>



// WEBPACK FOOTER //
// IssuesCategoryDetail.vue?1cfa70ae